import Immutable from 'immutable';
import omit from 'lodash/omit';
import {
  RawSubmittalCategory,
  SubmittalCategory,
} from 'records/SubmittalCategory';
import { RawSubmittalStatus, SubmittalStatus } from 'records/SubmittalStatus';
import {
  RawSubmittalReviewerResponse,
  SubmittalReviewerResponse,
} from './SubmittalReviewerResponse';

export enum SubmittalListTypes {
  ALL = 'ALL',
  MINE = 'MINE',
  VIEWABLE = 'VIEWABLE',
  APPROVED = 'APPROVED',
}

export type SubmittalId = string;

export interface RawSubmittal {
  id: SubmittalId;
  projectid: string;
  number: string;
  specsectionnumber: string;
  specsectionname: string;
  sectioncount: number;
  category: RawSubmittalCategory;
  description: string;
  status: RawSubmittalStatus;
  ballincourt: string;
  responsiblecompany: string;
  duefromsub: number;
  duefromreviewer: number;
  leadtime: string;
  requiredonsite: number;
  dateapproved: number;
  datecreated: number;
  createdby: string;
  attachments: string[];
  reviewerresponse: RawSubmittalReviewerResponse;
  subrespondedby: string;
  subrespondedon: number;
  reviewedby: string;
  reviewedon: number;
  subassignedby: string;
  subassignedon: number;
  reviewerassignedy: string;
  reviewerassignedon: number;
}

export interface SubmittalData {
  id: SubmittalId;
  projectid: string;
  number: string;
  specsectionnumber: string;
  specsectionname: string;
  sectioncount: number;
  category: SubmittalCategory;
  description: string;
  status: SubmittalStatus;
  ballincourt: string;
  responsiblecompany: string;
  duefromsub: number;
  duefromreviewer: number;
  leadtime: string;
  requiredonsite: number;
  dateapproved: number;
  datecreated: number;
  createdby: string;
  attachments: Immutable.List<string>; // DocId or PageId?
  reviewerresponse: SubmittalReviewerResponse;
  subrespondedby: string;
  subrespondedon: number;
  reviewedby: string;
  reviewedon: number;
  subassignedby: string;
  subassignedon: number;
  reviewerassignedy: string;
  reviewerassignedon: number;
}

const defaultValues: SubmittalData = {
  id: '',
  projectid: '',
  number: '',
  specsectionnumber: '',
  specsectionname: '',
  sectioncount: 0,
  category: new SubmittalCategory(),
  description: '',
  status: new SubmittalStatus(),
  ballincourt: '',
  responsiblecompany: '',
  duefromsub: 0,
  duefromreviewer: 0,
  leadtime: '',
  requiredonsite: 0,
  dateapproved: 0,
  datecreated: 0,
  createdby: '',
  attachments: Immutable.List(),
  reviewerresponse: new SubmittalReviewerResponse(),
  subrespondedby: '',
  subrespondedon: 0,
  reviewedby: '',
  reviewedon: 0,
  subassignedby: '',
  subassignedon: 0,
  reviewerassignedy: '',
  reviewerassignedon: 0,
};

export interface UpdateSubmittal {
  id: SubmittalId;
  projectid: string;
  specsectionnumber: string;
  specsectionname: string;
  category: string;
  description: string;
  status: string;
  ballincourt: string;
  responsiblecompany: string;
  duefromsub: number;
  duefromreviewer: number;
  leadtime: string;
  requiredonsite: number;
  dateapproved: number;
  reviewerresponse: string;
  subrespondedby: string;
  subrespondedon: number;
  reviewedby: string;
  reviewedon: number;
}

export const SubmittalFactory = Immutable.Record(defaultValues, 'Submittal');

export class Submittal extends SubmittalFactory {
  serialize(): RawSubmittal {
    return this.toJS();
  }

  toUpdateSubmittal(): UpdateSubmittal {
    const omittedSubmittal = omit(this.toJS(), [
      'number',
      'datecreated',
      'createdby',
      'sectioncount',
      'attachments',
      'subassignedon',
      'subassignedby',
      'reviewerassignedon',
      'reviewerassignedy',
    ]);

    return {
      ...omittedSubmittal,
      category: this.category.kind,
      status: this.status.kind,
      reviewerresponse: this.reviewerresponse.kind,
    };
  }

  static deserialize(rawSubmittal: RawSubmittal): Submittal {
    return new Submittal({
      ...rawSubmittal,
      status: new SubmittalStatus(rawSubmittal.status),
      category: new SubmittalCategory(rawSubmittal.category),
      attachments: Immutable.List(rawSubmittal.attachments),
      reviewerresponse: new SubmittalReviewerResponse(
        rawSubmittal.reviewerresponse
      ),
    });
  }
}

export type SelectedSubmittals = Immutable.Set<SubmittalId>;

export type SubmittalList = Immutable.List<Submittal>;

export type SubmittalOrderedMap = Immutable.OrderedMap<string, Submittal>;

export type ApprovedFileId = string;

export type SelectedApprovedFiles = Immutable.Set<string>;
